import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { JOB_TRANSITIONS, type FormInstance, type FormTemplate, type JobStatus } from '@worksy360/shared';
import { api, ApiRequestError } from '../lib/api.js';
import { useCustomer, useJob } from '../lib/hooks.js';
import { Badge, Button, Card, Dialog, EmptyState, Field, Input, Skeleton } from '../ui/index.js';
import { useToast } from '../ui/toast.js';
import { label, money, statusTone } from '../lib/format.js';
import { FormFill } from '../components/FormFill.js';

const ACTION_LABEL: Partial<Record<JobStatus, string>> = {
  dispatched: 'On my way', en_route: 'En route', arrived: 'Arrived', in_progress: 'Start job',
  paused: 'Pause', completed: 'Complete', scheduled: 'Schedule',
};

export function MobileJob() {
  const { cid = '', jid = '' } = useParams();
  const job = useJob(cid, jid);
  const customer = useCustomer(cid);
  const qc = useQueryClient();
  const toast = useToast();
  const templates = useQuery({ queryKey: ['form-templates'], queryFn: () => api.get<{ items: FormTemplate[] }>('/forms/templates') });
  const forms = useQuery({ queryKey: ['job-forms', cid, jid], queryFn: () => api.get<{ items: FormInstance[] }>(`/customers/${cid}/jobs/${jid}/forms`), enabled: !!cid && !!jid });
  const [busy, setBusy] = useState<string | null>(null);
  const [addMat, setAddMat] = useState(false);
  const [fillForm, setFillForm] = useState<FormTemplate | null>(null);

  const refresh = () => { qc.invalidateQueries({ queryKey: ['job', cid, jid] }); qc.invalidateQueries({ queryKey: ['myday'] }); qc.invalidateQueries({ queryKey: ['job-forms', cid, jid] }); };
  const act = async (key: string, fn: () => Promise<unknown>, msg?: string) => {
    setBusy(key);
    try { await fn(); refresh(); if (msg) toast.push(msg, 'success'); }
    catch (err) { toast.push(err instanceof ApiRequestError ? err.message : 'Action failed', 'error'); }
    finally { setBusy(null); }
  };

  if (job.isLoading) return <div className="mobile-job"><Card className="card-pad"><Skeleton h={100} /></Card></div>;
  if (job.error || !job.data) return <div className="mobile-job"><Card className="card-pad"><EmptyState icon="⚠️" title="Job not found" action={<Link to="/"><Button variant="secondary">Back to Today</Button></Link>} /></Card></div>;

  const j = job.data;
  const c = customer.data;
  const prop = c?.serviceLocations?.[0];
  const address = prop?.address ?? c?.billingAddress;
  const nexts = JOB_TRANSITIONS[j.status].filter((s) => s !== 'canceled');
  const mapsQuery = address ? encodeURIComponent(`${address.line1}, ${address.city} ${address.state}`) : '';

  return (
    <div className="mobile-job">
      <div className="page-head"><div><Link to="/" className="subtle" style={{ fontSize: 'var(--text-xs)' }}>← Today</Link><div className="page-title" style={{ fontSize: 'var(--text-xl)' }}>{j.title}</div><Badge tone={statusTone(j.status)}>{label(j.status)}</Badge></div></div>

      <Card className="card-pad" style={{ marginBottom: 'var(--sp-3)' }}>
        <strong>{c?.displayName}</strong>
        {address && <div className="muted" style={{ fontSize: 'var(--text-sm)' }}>{address.line1}, {address.city} {address.state}</div>}
        {c?.phones?.[0] && <div style={{ fontSize: 'var(--text-sm)', marginTop: 4 }}><a href={`tel:${c.phones[0]}`}>{c.phones[0]}</a></div>}
        {j.description && <p className="muted" style={{ fontSize: 'var(--text-sm)', marginTop: 8 }}>{j.description}</p>}
        {mapsQuery && <a href={`https://maps.google.com/?q=${mapsQuery}`} target="_blank" rel="noreferrer"><Button variant="secondary" block style={{ marginTop: 8 }}>Navigate</Button></a>}
      </Card>

      {/* Status actions — big tap targets */}
      <Card className="card-pad" style={{ marginBottom: 'var(--sp-3)' }}>
        {nexts.length === 0 ? <div className="muted" style={{ fontSize: 'var(--text-sm)' }}>Job {j.status}.</div>
          : nexts.map((s) => (
            <Button key={s} className="bigbtn" variant={s === 'completed' ? 'primary' : 'secondary'} loading={busy === `st${s}`}
              onClick={() => act(`st${s}`, () => api.post(`/customers/${cid}/jobs/${jid}/status`, { status: s }), `Marked ${ACTION_LABEL[s] ?? label(s)}`)}>
              {ACTION_LABEL[s] ?? label(s)}
            </Button>
          ))}
        {j.status === 'completed' && !j.invoiceId && (
          <Button className="bigbtn" loading={busy === 'inv'} onClick={() => act('inv', () => api.post(`/customers/${cid}/jobs/${jid}/invoice`), 'Invoice drafted')}>Create invoice</Button>
        )}
      </Card>

      {/* Checklist */}
      {j.checklist.length > 0 && (
        <Card className="card-pad" style={{ marginBottom: 'var(--sp-3)' }}>
          <strong style={{ fontSize: 'var(--text-sm)' }}>Checklist</strong>
          <div style={{ marginTop: 8 }}>
            {j.checklist.map((item) => (
              <div key={item.id} className={`check-row ${item.done ? 'done' : ''}`} onClick={() => act(`ck${item.id}`, () => api.post(`/customers/${cid}/jobs/${jid}/checklist/${item.id}`, { done: !item.done }))}>
                <span className="check-box">{item.done ? '✓' : ''}</span> {item.label}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Materials */}
      <Card className="card-pad" style={{ marginBottom: 'var(--sp-3)' }}>
        <div className="row between"><strong style={{ fontSize: 'var(--text-sm)' }}>Materials</strong><Button size="sm" variant="secondary" onClick={() => setAddMat(true)}>+ Add</Button></div>
        {j.materials.length === 0 ? <p className="muted" style={{ fontSize: 'var(--text-sm)', marginTop: 6 }}>None added.</p>
          : <div style={{ marginTop: 6 }}>{j.materials.map((m) => <div key={m.id} className="row between" style={{ padding: '4px 0' }}><span style={{ fontSize: 'var(--text-sm)' }}>{m.quantity}× {m.name}</span><span className="tabular">{money(m.unitPriceCents * m.quantity)}</span></div>)}</div>}
      </Card>

      {/* Forms */}
      <Card className="card-pad">
        <strong style={{ fontSize: 'var(--text-sm)' }}>Forms & checklists</strong>
        <div style={{ marginTop: 8 }}>
          {(forms.data?.items ?? []).map((fi) => <div key={fi.id} className="row between" style={{ padding: '4px 0' }}><span style={{ fontSize: 'var(--text-sm)' }}>{fi.templateName}</span><Badge tone={fi.completed ? 'success' : 'warning'}>{fi.completed ? 'Completed' : 'Draft'}</Badge></div>)}
          {(templates.data?.items ?? []).map((tpl) => <Button key={tpl.id} variant="secondary" block style={{ marginTop: 6 }} onClick={() => setFillForm(tpl)}>Fill: {tpl.name}</Button>)}
          {(templates.data?.items?.length ?? 0) === 0 && <p className="muted" style={{ fontSize: 'var(--text-sm)' }}>No form templates configured.</p>}
        </div>
      </Card>

      {addMat && <AddMaterialDialog onClose={() => setAddMat(false)} onAdd={(name, qty, price) => act('mat', () => api.post(`/customers/${cid}/jobs/${jid}/materials`, { name, quantity: qty, unitPriceCents: Math.round(price * 100) }), 'Material added').then(() => setAddMat(false))} />}
      {fillForm && <FormFill template={fillForm} customerId={cid} jobId={jid} onClose={() => setFillForm(null)} onDone={() => { setFillForm(null); refresh(); }} />}
    </div>
  );
}

function AddMaterialDialog({ onClose, onAdd }: { onClose: () => void; onAdd: (name: string, qty: number, price: number) => void }) {
  const [name, setName] = useState('');
  const [qty, setQty] = useState('1');
  const [price, setPrice] = useState('');
  return (
    <Dialog title="Add material" onClose={onClose}
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={() => onAdd(name, parseFloat(qty || '1') || 1, parseFloat(price || '0') || 0)} disabled={!name}>Add</Button></>}>
      <div className="stack gap-4">
        <Field label="Material"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Capacitor 45/5 MFD" autoFocus /></Field>
        <div className="row gap-3">
          <Field label="Qty"><Input value={qty} onChange={(e) => setQty(e.target.value)} style={{ width: 70 }} inputMode="decimal" /></Field>
          <Field label="Unit price ($)"><Input value={price} onChange={(e) => setPrice(e.target.value)} style={{ width: 120 }} inputMode="decimal" /></Field>
        </div>
      </div>
    </Dialog>
  );
}
